import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ConfigProvider, Tabs, Select, Space, Tag, Spin, Result, Button, Modal, Input, notification } from 'antd';
import { ExperimentOutlined, PlusOutlined } from '@ant-design/icons';
import './pm.css';
import pmApi from './pmApi';
import DesignerTab from './DesignerTab';
import BandsTab from './BandsTab';
import AnalyticsTab from './AnalyticsTab';
import MappingsTab from './MappingsTab';
import GuideTab from './GuideTab';
import TiersManagement from '../plans/TiersManagement';
import EnrollmentSegmentsSettings from '../../components/Settings/EnrollmentSegmentsSettings';
import AdminCheckinTypesList from '../../components/CheckinTypes/AdminCheckinTypesList';
import AdminCheckinTypeDetail from '../../components/CheckinTypes/AdminCheckinTypeDetail';
import AdminCareProgramsList from '../../components/CarePrograms/AdminCareProgramsList';
import AdminCareProgramDetail from '../../components/CarePrograms/AdminCareProgramDetail';
import PlanCatalogPanel from '../plans/PlanCatalogPanel';

/**
 * Plan Management workspace — the new plan model ("definition is
 * enforcement"), running in parallel with the legacy plan screens until
 * cutover. Everything here operates the pm_* greenfield tables via
 * /v2/admin/pm/* and cannot affect the legacy flow except through the
 * explicit, audited per-plan flips in Plan Design → Setup.
 *
 * Standalone route for the dark-launch phase; merged into the main plan
 * screens once stable.
 */

const modeTag = (plan) => {
  const mode = plan?.mode || 'ignore';
  if (mode === 'enforce_strict' || mode === 'enforce') return <Tag color="red" style={{ marginInlineEnd: 0 }}>Enforce Strictly</Tag>;
  if (mode === 'advisory' || mode === 'log') return <Tag color="gold" style={{ marginInlineEnd: 0 }}>Advisory</Tag>;
  return null;
};

export default function PlanManagementPage() {
  // Deep-link support: the Catalog tab's Design/Bands actions (and anything
  // else linking in from outside this workspace) pass ?plan=<id>&tab=<key>.
  const [searchParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [metaFailed, setMetaFailed] = useState(false);
  const [planId, setPlanId] = useState(() => {
    const fromUrl = Number(searchParams.get('plan'));
    return Number.isFinite(fromUrl) && fromUrl > 0 ? fromUrl : null;
  });
  const [tab, setTab] = useState(() => searchParams.get('tab') || 'catalog');
  const [catalogVersion, setCatalogVersion] = useState(0); // bumps when the Benefit Item tab creates/edits/deletes an item
  // Encounter types and care programs edit in place, inside their own tab —
  // 'new' or an id opens the detail view, null shows the list.
  const [openEncounterType, setOpenEncounterType] = useState(null);
  const [openCareProgram, setOpenCareProgram] = useState(null);
  const [newPlanOpen, setNewPlanOpen] = useState(false);
  const [newPlan, setNewPlan] = useState({ plan_name: '', plan_type: 'retail', plan_description: '' });
  const [creating, setCreating] = useState(false);

  const createPlan = async () => {
    if (!newPlan.plan_name.trim()) {
      notification.warning({ message: 'Plan name is required' });
      return;
    }
    setCreating(true);
    const res = await pmApi.createPlan(newPlan);
    setCreating(false);
    if (res.ok) {
      notification.success({ message: 'Plan created', description: res.message });
      setNewPlanOpen(false);
      setNewPlan({ plan_name: '', plan_type: 'retail', plan_description: '' });
      await loadMeta();
      setPlanId(res.data.plan_id);
      setTab('designer');
    }
  };

  const loadMeta = useCallback(async () => {
    const res = await pmApi.meta();
    if (res.ok) {
      setMeta(res.data);
      setMetaFailed(false);
    } else {
      setMetaFailed(true);
    }
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const plans = useMemo(() => meta?.plans || [], [meta]);
  const plan = useMemo(
    () => plans.find((p) => Number(p.plan_id) === Number(planId)) || null,
    [plans, planId]
  );

  if (metaFailed) {
    return (
      <Result
        status="warning"
        title="Plan Management is not reachable"
        subTitle="The pm_* migrations may not be applied on this environment, or your account lacks plan_designer.manage."
      />
    );
  }
  if (!meta) {
    return <div style={{ padding: 64, textAlign: 'center' }}><Spin size="large" /></div>;
  }

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#0e7a5f', borderRadius: 7 } }}>
      <div className="pm-workspace">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <span className="pm-eyebrow"><ExperimentOutlined /> New plan model · parallel run</span>
            <h2 className="pm-title">Plan Management</h2>
            <div className="pm-sub">
              Designing a plan is designing its limits: coverage rows, pools and windows are
              the same rows enforcement reads. Nothing here touches live operations until a
              plan&apos;s switchboard flag is deliberately flipped.
            </div>
          </div>
          <Space size={8} align="center">
            {plan ? modeTag(plan) : null}
            {plan?.plan_type ? <Tag color="green" style={{ marginInlineEnd: 0 }}>{plan.plan_type}</Tag> : null}
            <Button icon={<PlusOutlined />} onClick={() => setNewPlanOpen(true)}>New plan</Button>
            <Select
              showSearch
              style={{ minWidth: 260 }}
              placeholder="Pick a plan…"
              value={planId}
              onChange={setPlanId}
              optionFilterProp="label"
              options={plans.map((p) => ({
                value: p.plan_id,
                label: `${p.plan_name}${Number(p.coverage_rows) > 0 ? ` · ${p.coverage_rows} rows` : ''}${p.mode && p.mode !== 'ignore' && p.mode !== 'off' ? ` · ${p.mode === 'enforce_strict' ? 'enforcing' : p.mode}` : ''}`,
              }))}
            />
          </Space>
        </div>

        <Tabs
          activeKey={tab}
          onChange={setTab}
          style={{ marginTop: 8 }}
          items={[
            {
              key: 'catalog',
              label: 'Catalog',
              children: (
                <PlanCatalogPanel
                  pmPlans={plans}
                  onPlanBandsChanged={loadMeta}
                  onOpenDesigner={(plan) => { setPlanId(plan?.plan_id ?? plan?.id); setTab('designer'); }}
                  onOpenBands={(plan) => { setPlanId(plan?.plan_id ?? plan?.id); setTab('bands'); }}
                />
              ),
            },
            {
              key: 'designer',
              label: 'Designer',
              children: (
                <DesignerTab planId={planId} plan={plan} plans={plans} meta={meta} onDesignChanged={loadMeta} catalogVersion={catalogVersion} />
              ),
            },
            {
              key: 'bands',
              label: 'Bands & Pricing',
              children: <BandsTab planId={planId} plan={plan} />,
            },
            {
              key: 'analytics',
              label: 'Analytics',
              children: <AnalyticsTab meta={meta} />,
            },
            {
              key: 'mappings',
              label: 'Benefit Item Mapping',
              children: <MappingsTab meta={meta} onCatalogChanged={() => setCatalogVersion((v) => v + 1)} />,
            },
            {
              key: 'tiers',
              label: 'Tiers',
              children: <TiersManagement />,
            },
            {
              key: 'enrollment-segments',
              label: 'Segment rules',
              children: <EnrollmentSegmentsSettings embedded />,
            },
            {
              key: 'encounter-types',
              label: 'Encounter Types',
              children: openEncounterType ? (
                <AdminCheckinTypeDetail
                  key={openEncounterType}
                  checkinTypeId={openEncounterType}
                  onBack={() => setOpenEncounterType(null)}
                  onCreated={(newId) => setOpenEncounterType(newId)}
                />
              ) : (
                <AdminCheckinTypesList
                  embedded
                  onOpenType={(id) => setOpenEncounterType(id)}
                  onCreateType={() => setOpenEncounterType('new')}
                />
              ),
            },
            {
              key: 'care-programs',
              label: 'Care Programs',
              children: openCareProgram ? (
                <AdminCareProgramDetail
                  key={openCareProgram}
                  programId={openCareProgram}
                  onBack={() => setOpenCareProgram(null)}
                  onCreated={(newId) => setOpenCareProgram(newId)}
                />
              ) : (
                <AdminCareProgramsList
                  embedded
                  onOpenProgram={(id) => setOpenCareProgram(id)}
                  onCreateProgram={() => setOpenCareProgram('new')}
                />
              ),
            },
            {
              key: 'guide',
              label: 'Guide',
              children: <GuideTab />,
            },
          ]}
        />
      </div>

      <Modal
        open={newPlanOpen}
        title="New plan (draft — not sellable until launch)"
        onCancel={() => setNewPlanOpen(false)}
        onOk={createPlan}
        okText="Create draft plan"
        confirmLoading={creating}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size={10}>
          <Input
            placeholder="Plan name (e.g. Emerald)"
            value={newPlan.plan_name}
            onChange={(e) => setNewPlan((s) => ({ ...s, plan_name: e.target.value }))}
          />
          <Select
            style={{ width: '100%' }}
            value={newPlan.plan_type}
            onChange={(v) => setNewPlan((s) => ({ ...s, plan_type: v }))}
            options={[
              { value: 'retail', label: 'Retail' },
              { value: 'corporate', label: 'Corporate' },
              { value: 'sme', label: 'SME' },
              { value: 'utility', label: 'Utility' },
            ]}
          />
          <Input.TextArea
            placeholder="Description (optional)"
            value={newPlan.plan_description}
            onChange={(e) => setNewPlan((s) => ({ ...s, plan_description: e.target.value }))}
            autoSize={{ minRows: 2, maxRows: 4 }}
          />
          <div className="pm-consequence" style={{ marginBottom: 0 }}>
            Created as a <b>draft, non-sellable</b> plan: it appears nowhere clients or
            organizations can buy it. Design its coverage, price its bands, publish —
            selling it is a separate, deliberate act at launch.
          </div>
        </Space>
      </Modal>
    </ConfigProvider>
  );
}
